import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";
import { useLocale, pickLocalized } from "@/contexts/LocaleContext";
import { useSettings } from "@/contexts/SettingsContext";
import { Calendar, Clock, ArrowRight, Ticket, Wine } from "lucide-react";

function formatDateTime(iso, locale) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat(locale === "hr" ? "hr-HR" : "en-GB", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Zagreb",
    }).format(d);
  } catch {
    return iso;
  }
}

export default function EventDetail() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get(`/public/events/${slug}`)
      .then((r) => setEv(r.data))
      .catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <PublicLayout pageTitle={t("events_page.title")} testid="event-detail-error">
        <section className="max-w-3xl mx-auto px-5 sm:px-8 py-24 text-center">
          <h1 className="font-display text-4xl">404</h1>
          <p className="text-muted-foreground mt-3">Event not found.</p>
          <Link to="/events" className="inline-block mt-6 text-primary hover:underline">
            ← {t("nav.events")}
          </Link>
        </section>
      </PublicLayout>
    );
  }

  if (!ev) {
    return <PublicLayout pageTitle="…"><div className="py-24" /></PublicLayout>;
  }

  const title = pickLocalized(ev.title, locale);
  const descHtml = pickLocalized(ev.description_html, locale);

  // JSON-LD Event
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: title,
    startDate: ev.starts_at,
    endDate: ev.ends_at,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    image: ev.graphic_url ? [ev.graphic_url] : [],
    location: {
      "@type": "Place",
      name: settings?.business_name || "King's Caffe Bar",
      address: pickLocalized(settings?.address, locale),
    },
    description: descHtml?.replace(/<[^>]+>/g, "").slice(0, 300),
    offers: ev.booking_mode === "ticket_and_table" && ev.ticket_price > 0
      ? { "@type": "Offer", price: ev.ticket_price, priceCurrency: "EUR", availability: "https://schema.org/InStock" }
      : undefined,
  };

  return (
    <PublicLayout pageTitle={title} pageDescription={descHtml?.replace(/<[^>]+>/g, "").slice(0, 160)} testid="event-detail-page">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <section className="relative overflow-hidden" data-testid="event-detail-hero">
        {ev.graphic_url && (
          <div
            className="absolute inset-0 -z-10"
            style={{
              backgroundImage: `linear-gradient(to bottom, rgba(10,10,10,0.5), rgba(10,10,10,0.95)), url(${ev.graphic_url})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        )}
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
          <p className="text-xs uppercase tracking-[0.3em] text-primary">— {t("nav.events")}</p>
          <h1 className="font-display text-5xl sm:text-7xl mt-3 leading-[0.95]" data-testid="event-title">
            {title}
          </h1>
          <div className="flex flex-wrap items-center gap-3 mt-5 text-sm">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/40 text-primary">
              <Calendar className="w-3.5 h-3.5" /> {formatDateTime(ev.starts_at, locale)}
            </span>
            {ev.booking_mode === "ticket_and_table" && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-muted-foreground">
                <Ticket className="w-3.5 h-3.5" /> €{Number(ev.ticket_price).toFixed(2)}
              </span>
            )}
          </div>
          {ev.booking_mode !== "no_booking" && (
            <button
              type="button"
              onClick={() => navigate(`/booking?event=${ev.slug}`)}
              className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:bg-primary/90 gold-glow"
              data-testid="event-book-cta"
            >
              {t("common.reserve_table")} <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
        <div
          className="prose prose-invert max-w-none text-lg leading-relaxed [&_a]:text-primary"
          dangerouslySetInnerHTML={{ __html: descHtml || "" }}
          data-testid="event-description"
        />
      </section>

      {ev.beverage_packages?.length > 0 && (
        <section className="border-t border-border/60 bg-card/30" data-testid="event-packages-section">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
            <p className="text-xs uppercase tracking-[0.3em] text-primary">— Packages</p>
            <h2 className="font-display text-3xl sm:text-4xl mt-3">Paketi / Packages</h2>
            <div className="grid sm:grid-cols-2 gap-5 mt-8">
              {ev.beverage_packages.map((p, idx) => (
                <div key={idx} className="border border-border rounded-xl p-6 bg-background" data-testid={`event-package-${idx}`}>
                  <Wine className="w-6 h-6 text-primary mb-3" />
                  <h3 className="font-display text-xl">{pickLocalized(p.title, locale)}</h3>
                  <p className="text-sm text-muted-foreground mt-2">{pickLocalized(p.description, locale)}</p>
                  <div className="text-primary font-medium mt-4">€{Number(p.price).toFixed(2)}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {ev.highlighted_products?.length > 0 && (
        <section data-testid="event-highlights-section">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
            <p className="text-xs uppercase tracking-[0.3em] text-primary">— Highlights</p>
            <h2 className="font-display text-3xl sm:text-4xl mt-3">Izdvojeno / Highlighted</h2>
            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              {ev.highlighted_products.map((p, idx) => (
                <div key={idx} className="border border-border rounded-xl p-4 bg-card" data-testid={`event-product-${idx}`}>
                  <div className="font-display text-lg">{pickLocalized(p.name, locale)}</div>
                  <div className="text-primary text-sm mt-2">€{Number(p.price).toFixed(2)}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </PublicLayout>
  );
}
